const TOLERANCE_NUMBER = 5;
const MAX_SPARSE_WORD = 3;

// get the first big chunk of phrase and invalidate the rest
const getFirstSinglePhrase = (foundWords) => {
  let lastValidWordIndex = null;

  foundWords.find((word, index) => {
    let isNextItemsValid = false;
    for (let i = 1; i <= TOLERANCE_NUMBER; i++) {
      if (foundWords[index + i]?.text) {
        isNextItemsValid = true;
        break;
      }
    }

    
    if (!isNextItemsValid) {
      lastValidWordIndex = index;
      return word
    };
  })

  return foundWords.map((word, index) => {
    if (index > lastValidWordIndex) {
      return undefined;
    }
    return word;
  })
}

// if words are too far between, invalidate
const getIsTooSparseWords = (words) => {
  let validWordsSequenceStartIndex = 0;
  words.find((word, index) => {
    if(word?.text) {
      validWordsSequenceStartIndex = index;
      return true;
    }
  });
  
  let isTooSparseWords = true;
  for (let i = 1; i <= MAX_SPARSE_WORD; i++) {
    if (words[validWordsSequenceStartIndex + i]?.text) {
      isTooSparseWords = false;
      break;
    }
  }

  return isTooSparseWords;
}

// if reading is too far ahead from the start of the phrase, invalidate the reading.
const getIsReadingTooFarAhead = (words) => {
  let isReadingTooFarAhead = true;
  for (let i = 0; i < TOLERANCE_NUMBER; i++) {
    if (words[i]?.text) {
      isReadingTooFarAhead = false;
      break;
    }
  };

  return isReadingTooFarAhead;
}

// A lone "e"/"a" from the previous clip was finishing the next sentence.
// Tail shortcut only on long phrases, and only for a real word (4+ letters).
const TAIL_WORD_MIN = 4;

const getIsReadingTooCloseToEnd = (foundWords) => {
  const start = Math.max(0, foundWords.length - TOLERANCE_NUMBER);
  for (let index = start; index < foundWords.length; index += 1) {
    const text = foundWords[index]?.text?.replace(/[^a-zA-Z]/g, "") || "";
    if (text.length >= TAIL_WORD_MIN) return true;
  }
  return false;
}

const fillGapWords = (words, singlePhrase, isReadingTooCloseToEnd = false) => {
  let lastValidWordIndex = undefined;

  for (let i = singlePhrase.length - 1; i >= 0; i--) {
    if (singlePhrase[i]?.text) {
      lastValidWordIndex = i;
      break;
    }
  };
  
  const mergedWords = singlePhrase.map((word, index) => {
    const originalWord = words[index];
    if(index > lastValidWordIndex && !isReadingTooCloseToEnd) {
      return {
        ...originalWord,
        isValid: false,
      }
    }
    return {
      ...originalWord,
      isValid: true,
    }
  })

  return mergedWords;
}

const mergeInvalidWords = (words, foundWords, phraseLength) => {
  const tailCanFinishPhrase = phraseLength > TOLERANCE_NUMBER * 2
    && getIsReadingTooCloseToEnd(foundWords);
  if (tailCanFinishPhrase) {
    return fillGapWords(words, foundWords, true);
  }

  const singlePhrase = getFirstSinglePhrase(foundWords);
  const isTooSparseWords = getIsTooSparseWords(singlePhrase);
  const isReadingTooFarAhead = getIsReadingTooFarAhead(singlePhrase);

  if (isTooSparseWords || isReadingTooFarAhead) {
    return words;
  }

  const filledGapWords = fillGapWords(words, singlePhrase, false);

  return filledGapWords;
};

const mergeWords = (words, validatedWords) => {
  const mergedWords = words.map((word) => {
    const validatedWord = validatedWords.find(validatedWord => validatedWord.id == word.id)
    if(validatedWord?.isValid) return validatedWord;
    return word;
  })
  return mergedWords;
}

const validateAllWords = (words) => (
  words.map(word => ({ ...word, isValid: true }))
)

const getHasNewValidWords = (words, newWords) => {
  const wordsAmount = words.filter(word => word.isValid).length;
  const newWordsAmount = newWords.filter(word => word.isValid).length;

  return newWordsAmount > wordsAmount;
}

const tokenOf = (text) => String(text).replace(/[^a-zA-Z]/g, '').toLowerCase();

// The previous sentence's tail ("estou sozinha") was closing the next phrase
// before its own last word had been read.
export function isBorrowedTailFinish(words, transcription, previousSpoken) {
  if (!previousSpoken || words.some((word) => word.isValid)) return false;
  if (words.length <= TOLERANCE_NUMBER * 2) return false;

  const heard = new Set(String(transcription).split(/\s+/).map(tokenOf).filter(Boolean));
  const previous = new Set(
    String(previousSpoken).split(/\s+/).map(tokenOf).filter((token) => token.length >= TAIL_WORD_MIN),
  );
  const borrowed = words.slice(-TOLERANCE_NUMBER).some((word) => {
    const token = tokenOf(word.text);
    return token.length >= TAIL_WORD_MIN && previous.has(token);
  });
  if (!borrowed) return false;

  const last = [...words].reverse().find((word) => tokenOf(word.text).length >= TAIL_WORD_MIN);
  return last ? !heard.has(tokenOf(last.text)) : false;
}

export const validateWords = (words, transcription) => {
  // console.log('>>> words', words)
  // find spoken words
  let transcriptionWordByWord = transcription.split(' ');

  const isShortParagraph = words.length < TOLERANCE_NUMBER;
  if (isShortParagraph) {
    return {
      validatedWords: validateAllWords(words),
      hasNewValidWords: true,
    }
  }

  const invalidWords = words.filter(word => (!word.isValid))

  const foundWords = invalidWords.map(word => {
    let hasSpokenWord = false;
    let indexFound = undefined;
    const formattedWord = word.text.replace(/[^a-zA-Z ]/g, "").toLowerCase();

    transcriptionWordByWord.find((transcriptionWord, index) => {
      const formattedTranscriptionWord = transcriptionWord.replace(/[^a-zA-Z ]/g, "").toLowerCase();
      const isValidWord = formattedTranscriptionWord == formattedWord;

      if (isValidWord) {
        hasSpokenWord = true;
        indexFound = index;
        return;
      }
    })

    if (hasSpokenWord && indexFound !== undefined) {
      transcriptionWordByWord.splice(indexFound, 1);
      indexFound = undefined;
      return word;
    };
  })

  const validatedWords = mergeInvalidWords(invalidWords, foundWords, words.length);
  const mergedWords = mergeWords(words, validatedWords);

  getHasNewValidWords(words, mergedWords);
  return {
    validatedWords: mergedWords,
    hasNewValidWords: getHasNewValidWords(words, mergedWords),
  };
}